import { Component, computed, inject, OnDestroy } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { CalculationPastStoreService } from "@bitwarden/web-vault/app/services/store/calculation-past/calculation-past.store.service";
import { CalculationTodayStoreService } from "@bitwarden/web-vault/app/services/store/calculation-today/calculation-today.store.service";
import { ViewsService } from "@bitwarden/web-vault/app/services/views/views.service";
import { View } from "@bitwarden/web-vault/app/models/enum/view.enum";
import { ScenarioStoreService } from "@bitwarden/web-vault/app/services/store/scenario/scenario.store.service";
import { DialogWrapperComponent } from "@bitwarden/web-vault/app/components/wrappers/dialog-wrapper/dialog-wrapper.component";
import { RIGHT_PANEL_CONFIG } from "@bitwarden/web-vault/app/models/constants/global.constants";
import { ViewsDropdownComponent } from "@bitwarden/web-vault/app/gloss/views/views-control/views-dropdown/views-dropdown.component";
// Global, unencapsulated stylesheet: injected via this side-effect import, not Angular's
// styleUrls/styleUrl, so it isn't scoped to this component.
import "./views-customize.component.scss";

@Component({
  selector: "app-views-customize",
  templateUrl: "./views-customize.component.html",
})
export class ViewsCustomizeComponent implements OnDestroy {
  private readonly unsubscribe$ = new Subject<void>();

  protected calculationPastStoreService: CalculationPastStoreService = inject(
    CalculationPastStoreService,
  );
  protected calculationTodayStoreService: CalculationTodayStoreService = inject(
    CalculationTodayStoreService,
  );
  protected scenarioStoreService: ScenarioStoreService = inject(ScenarioStoreService);
  protected viewsService: ViewsService = inject(ViewsService);
  protected dialog: MatDialog = inject(MatDialog);
  protected readonly View = View;

  viewsDropdown = false;

  // Configuration for filter types
  private readonly FILTER_KEYS = {
    INSTITUTIONS: "institutions",
    ACCOUNTS: "accounts",
    CATEGORIES: "categories",
  } as const;

  private getStoreService() {
    const selectedView = this.viewsService.currentView();
    switch (selectedView) {
      case View.FUTURE:
        return this.scenarioStoreService;
      case View.PAST:
        return this.calculationPastStoreService;
      case View.TODAY:
        return this.calculationTodayStoreService;
      default:
        return null;
    }
  }

  hasSelection = computed(() => {
    return this.getStoreService()?.filters.selected();
  });

  private getCommonFilterCount(
    filterKey: typeof this.FILTER_KEYS.INSTITUTIONS | typeof this.FILTER_KEYS.ACCOUNTS,
  ): number {
    const storeService = this.getStoreService();
    const selected = storeService?.filters.selected();
    return selected?.[filterKey]?.length || 0;
  }

  institutionCount = computed(() => {
    return this.getCommonFilterCount(this.FILTER_KEYS.INSTITUTIONS);
  });

  accountCount = computed(() => {
    return this.getCommonFilterCount(this.FILTER_KEYS.ACCOUNTS);
  });

  categoryCount = computed(() => {
    const selectedView = this.viewsService.currentView();
    if (selectedView !== View.PAST) {
      return 0;
    }
    const selected = this.calculationPastStoreService.filters.selected();
    return selected?.categories?.length || 0;
  });

  private isCommonFilterAllSelected(
    filterKey: typeof this.FILTER_KEYS.INSTITUTIONS | typeof this.FILTER_KEYS.ACCOUNTS,
  ): boolean {
    const storeService = this.getStoreService();
    if (!storeService) {
      return true;
    }

    const selected = storeService.filters.selected();
    const options = storeService.filters.options();

    if (!selected || !options) {
      return true;
    }

    return selected[filterKey]?.length === options[filterKey]?.length;
  }

  isAllInstitutionsSelected = computed(() => {
    return this.isCommonFilterAllSelected(this.FILTER_KEYS.INSTITUTIONS);
  });

  isAllAccountsSelected = computed(() => {
    return this.isCommonFilterAllSelected(this.FILTER_KEYS.ACCOUNTS);
  });

  isAllCategoriesSelected = computed(() => {
    const selectedView = this.viewsService.currentView();
    if (selectedView !== View.PAST) {
      return true;
    }

    const selected = this.calculationPastStoreService.filters.selected();
    const options = this.calculationPastStoreService.filters.options();

    if (!selected || !options) {
      return true;
    }

    return selected.categories?.length === options.categories?.length;
  });

  filterPills = computed(() => {
    const pills: { label: string; count: number }[] = [];

    if (!this.isAllInstitutionsSelected()) {
      pills.push({ label: this.FILTER_KEYS.INSTITUTIONS, count: this.institutionCount() });
    }
    if (!this.isAllAccountsSelected()) {
      pills.push({ label: this.FILTER_KEYS.ACCOUNTS, count: this.accountCount() });
    }
    if (this.viewsService.currentView() === View.PAST && !this.isAllCategoriesSelected()) {
      pills.push({ label: this.FILTER_KEYS.CATEGORIES, count: this.categoryCount() });
    }

    return pills;
  });

  openFilterDialog() {
    const dialogRef = this.dialog.open(DialogWrapperComponent, {
      ...RIGHT_PANEL_CONFIG,
      data: {
        title: "selectFiltersBelow",
        showBackButton: false,
        component: ViewsDropdownComponent,
        componentInputs: {
          closeDialog: () => dialogRef.close(),
        },
      },
    });
    this.viewsDropdown = true;
    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.viewsDropdown = false;
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
